import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useOptionalActiveOrganization, useOrgPath } from "@stores/ActiveOrganizationContext";
import { useAuthContext } from "@stores/AuthContext";
import { getIsAdmin, getIsDiscoverAdmin } from "@stores/utils";
import { Coins, LogOutV2, Play, Settings } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekDivider, PeekPaper, PeekStack } from "@piiqtechnologies/ui-components";
import { usePolicyContext } from "@piiqtechnologies/ui-lib";
import { DiscoverAction } from "@constants/Policies";
import { NavigationListItem, NavigationLogo, NavigationTitle } from "./components";
import { StorylaneTourDialog } from "../storylane-tour-dialog";
import { PATHS } from "@constants/Paths";
import { resolveRoute } from "@analytics-setup/routeTracking";
import { AnalyticsArea, useBillingEnabled, useIsEnterprise, useTrackEvent } from "@hooks";
import messages from "./messages.i18n";
export const NavigationBar = ({ title, titleUpperCase = true, headerContent, showDivider = true, scrollableContent = false, children, }) => {
    var _a, _b, _c;
    const intl = useIntl();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // Both are org-scoped pages now, not ?view= overlays on whatever path the bar happens to sit
    // on. Staff and the public results page have no active org, and there orgPath is the identity.
    const orgPath = useOrgPath();
    const track = useTrackEvent();
    const billingEnabled = useBillingEnabled();
    const { onLogout, user } = useAuthContext();
    const isDiscoverAdmin = getIsDiscoverAdmin(user);
    const canManageUsers = usePolicyContext(DiscoverAction.ManageUsers);
    const isPeekAdmin = getIsAdmin(user);
    const [isTourOpen, setIsTourOpen] = useState(false);
    const isPlansBillingOpen = pathname === orgPath(PATHS.PLANS_BILLING);
    const isAccountSettingsOpen = pathname === orgPath(PATHS.ACCOUNT_SETTINGS);
    const isPeekStaff = Boolean(((_a = user === null || user === void 0 ? void 0 : user.email) === null || _a === void 0 ? void 0 : _a.toLowerCase().endsWith("@peek.us")) && !((_b = user === null || user === void 0 ? void 0 : user.email) === null || _b === void 0 ? void 0 : _b.includes("+")));
    // Per-organization, not account-level: dev added this check, and dropping it would offer
    // billing to someone who administers another organization but only reads this one.
    const canManageBilling = usePolicyContext(DiscoverAction.ManageBilling);
    const canManagePlansBilling = billingEnabled && isDiscoverAdmin && canManageBilling && !isPeekStaff;
    const activeOrgId = (_c = useOptionalActiveOrganization()) === null || _c === void 0 ? void 0 : _c.activeOrgId;
    const isEnterprise = useIsEnterprise(activeOrgId, canManagePlansBilling);
    const canAccessPlansBilling = canManagePlansBilling && !isEnterprise;
    const handleLogout = () => {
        onLogout();
        navigate(PATHS.LOGIN, { replace: true });
    };
    const handleAccountSettingsClick = () => {
        track(AnalyticsArea.UserSettings, "Clicked", { routeName: resolveRoute(pathname).routeName });
        navigate(orgPath(PATHS.ACCOUNT_SETTINGS));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekPaper, { elevation: 0, sx: {
                top: 0,
                width: 170,
                height: "100%",
                position: "sticky",
            } },
            React.createElement(PeekStack, { sx: { px: 1, py: 2, overflow: "hidden" }, height: "-webkit-fill-available", justifyContent: "space-between" },
                React.createElement(PeekStack, { direction: "column", sx: { flex: 1, minHeight: 0 } },
                    React.createElement(NavigationLogo, null),
                    React.createElement(PeekStack, { gap: 1, mt: 3 },
                        React.createElement(NavigationTitle, { isUpperCase: titleUpperCase, content: title !== null && title !== void 0 ? title : intl.formatMessage(messages.reportNavigationTitle) }),
                        headerContent),
                    React.createElement(PeekStack, { direction: "row", width: "100%", gap: 1, sx: scrollableContent ? { flex: 1, minHeight: 0 } : undefined },
                        showDivider && React.createElement(PeekDivider, { light: true, orientation: "vertical", sx: { height: "auto" } }),
                        React.createElement(PeekStack, { gap: 2, width: "100%", mt: 2, pr: 1, sx: scrollableContent
                                ? {
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: "auto",
                                    scrollbarWidth: "thin",
                                }
                                : undefined }, children))),
                React.createElement(PeekStack, { gap: 1 },
                    React.createElement(PeekDivider, { orientation: "horizontal", light: true }),
                    React.createElement(PeekBox, { sx: { ml: 1 } },
                        !isPeekAdmin && (React.createElement(NavigationListItem, { onClick: () => setIsTourOpen(true), icon: React.createElement(Play, { width: 12 }), label: intl.formatMessage(messages.aiDiscoverabilityDashboardReplayTour) })),
                        canAccessPlansBilling && (React.createElement(NavigationListItem, { onClick: () => navigate(orgPath(PATHS.PLANS_BILLING)), icon: React.createElement(Coins, { width: 15, height: 15 }), isActive: isPlansBillingOpen, label: intl.formatMessage(messages.aiDiscoverabilityDashboardPlansBilling) })),
                        canManageUsers && !isPeekStaff && (React.createElement(NavigationListItem, { onClick: handleAccountSettingsClick, icon: React.createElement(Settings, { width: 15, height: 15 }), isActive: isAccountSettingsOpen, label: intl.formatMessage(messages.aiDiscoverabilityDashboardSettings) })),
                        React.createElement(NavigationListItem, { onClick: handleLogout, icon: React.createElement(LogOutV2, null), label: intl.formatMessage(messages.aiDiscoverabilityDashboardLogout) }))))),
        React.createElement(StorylaneTourDialog, { open: isTourOpen, onClose: () => setIsTourOpen(false), analyticsArea: AnalyticsArea.Sidebar, analyticsAction: "Tour", trackProps: { routeName: resolveRoute(location.pathname).routeName } })));
};
export default NavigationBar;
